<template>
  <div class="admin-allocation-chart">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <LoadingIndicatorComponent message="Loading allocation data..." />
    </div>

    <!-- Chart Container -->
    <div v-else-if="allocationData" class="chart-container">
      <div class="chart-title">
        <h4 class="font-serif">
          Production Allocation Strategy (Next 12 Months)
        </h4>
        <p class="text-muted mb-0">
          Rolling 12-month forecast of how production will be allocated across
          sales strategies (based on current portfolio configurations)
        </p>
      </div>

      <StackedBarChart
        :chartData="chartData"
        :height="400"
        yAxisTitle="Production (MWh)"
        :showLegend="false"
      />

      <!-- Allocation Legend -->
      <div class="allocation-legend mt-4">
        <div class="row g-3">
          <!-- Total Card -->
          <div class="col-sm-6 col-md-4">
            <div class="legend-item legend-item-total">
              <div
                class="d-flex align-items-center justify-content-center mb-1"
              >
                <span
                  class="legend-color"
                  style="background-color: rgba(107, 114, 128, 0.8)"
                ></span>
                <span class="legend-label">Total Production</span>
              </div>
              <div class="legend-value">
                {{ formatNumber(allocationTotals.total / 1000) }} GWh
              </div>
              <div class="legend-percentage">100.00%</div>
            </div>
          </div>

          <!-- PPA Card -->
          <div class="col-sm-6 col-md-4">
            <div class="legend-item">
              <div
                class="d-flex align-items-center justify-content-center mb-1"
              >
                <span
                  class="legend-color"
                  :style="{
                    backgroundColor:
                      PALETTE.semanticOverrides.portfolioType['PPA'],
                  }"
                ></span>
                <span class="legend-label">PPA</span>
              </div>
              <div class="legend-value">
                {{ formatNumber(allocationTotals.ppa / 1000) }} GWh
              </div>
              <div class="legend-percentage">
                {{ formatPercentage(allocationTotals.ppaPercentage) }}
              </div>
              <button
                v-if="topCompanies.ppa.length > 0"
                class="btn btn-sm btn-outline-primary mt-2 w-100"
                @click="openModal('PPA', topCompanies.ppa)"
              >
                <FontAwesomeIcon icon="list" class="me-1" />
                View Top {{ topCompanies.ppa.length }}
              </button>
            </div>
          </div>

          <!-- Forward Card -->
          <div class="col-sm-6 col-md-4">
            <div class="legend-item">
              <div
                class="d-flex align-items-center justify-content-center mb-1"
              >
                <span
                  class="legend-color"
                  :style="{
                    backgroundColor:
                      PALETTE.semanticOverrides.portfolioType['Forward'],
                  }"
                ></span>
                <span class="legend-label">Forward</span>
              </div>
              <div class="legend-value">
                {{ formatNumber(allocationTotals.forward / 1000) }} GWh
              </div>
              <div class="legend-percentage">
                {{ formatPercentage(allocationTotals.forwardPercentage) }}
              </div>
              <button
                v-if="topCompanies.forward.length > 0"
                class="btn btn-sm btn-outline-primary mt-2 w-100"
                @click="openModal('Forward', topCompanies.forward)"
              >
                <FontAwesomeIcon icon="list" class="me-1" />
                View Top {{ topCompanies.forward.length }}
              </button>
            </div>
          </div>

          <!-- Self-Managed Card -->
          <div class="col-sm-6 col-md-4">
            <div class="legend-item">
              <div
                class="d-flex align-items-center justify-content-center mb-1"
              >
                <span
                  class="legend-color"
                  :style="{
                    backgroundColor:
                      PALETTE.semanticOverrides.portfolioType['Self-managed'],
                  }"
                ></span>
                <span class="legend-label">Self-Managed</span>
              </div>
              <div class="legend-value">
                {{ formatNumber(allocationTotals.selfManaged / 1000) }} GWh
              </div>
              <div class="legend-percentage">
                {{ formatPercentage(allocationTotals.selfManagedPercentage) }}
              </div>
              <button
                v-if="topCompanies.selfManaged.length > 0"
                class="btn btn-sm btn-outline-primary mt-2 w-100"
                @click="openModal('Self-Managed', topCompanies.selfManaged)"
              >
                <FontAwesomeIcon icon="list" class="me-1" />
                View Top {{ topCompanies.selfManaged.length }}
              </button>
            </div>
          </div>

          <!-- Spot Market Price Card -->
          <div class="col-sm-6 col-md-4">
            <div class="legend-item">
              <div
                class="d-flex align-items-center justify-content-center mb-1"
              >
                <span
                  class="legend-color"
                  :style="{
                    backgroundColor:
                      PALETTE.semanticOverrides.portfolioType['Spot Sales'],
                  }"
                ></span>
                <span class="legend-label">Spot Market Price</span>
              </div>
              <div class="legend-value">
                {{ formatNumber(allocationTotals.spotMarketPrice / 1000) }} GWh
              </div>
              <div class="legend-percentage">
                {{
                  formatPercentage(allocationTotals.spotMarketPricePercentage)
                }}
              </div>
              <button
                v-if="topCompanies.spotMarketPrice.length > 0"
                class="btn btn-sm btn-outline-primary mt-2 w-100"
                @click="
                  openModal('Spot Market Price', topCompanies.spotMarketPrice)
                "
              >
                <FontAwesomeIcon icon="list" class="me-1" />
                View Top {{ topCompanies.spotMarketPrice.length }}
              </button>
            </div>
          </div>

          <!-- Spot Min Price Card -->
          <div class="col-sm-6 col-md-4">
            <div class="legend-item">
              <div
                class="d-flex align-items-center justify-content-center mb-1"
              >
                <span
                  class="legend-color"
                  :style="{ backgroundColor: PALETTE.categorical[3] }"
                ></span>
                <span class="legend-label">Spot Min Price</span>
              </div>
              <div class="legend-value">
                {{ formatNumber(allocationTotals.spotMinPrice / 1000) }} GWh
              </div>
              <div class="legend-percentage">
                {{ formatPercentage(allocationTotals.spotMinPricePercentage) }}
              </div>
              <button
                v-if="topCompanies.spotMinPrice.length > 0"
                class="btn btn-sm btn-outline-primary mt-2 w-100"
                @click="openModal('Spot Min Price', topCompanies.spotMinPrice)"
              >
                <FontAwesomeIcon icon="list" class="me-1" />
                View Top {{ topCompanies.spotMinPrice.length }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <p class="text-muted">No allocation data available</p>
    </div>

    <!-- Top Companies Modal -->
    <AdminAllocationTopCompaniesModal
      :modalId="modalId"
      :allocationType="currentAllocationType"
      :companies="currentModalCompanies"
    />
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import AdminAllocationTopCompaniesModal from "@/admin/AdminAllocationTopCompaniesModal.vue";
import StackedBarChart from "@/common/StackedBarChart.vue";
import { PALETTE } from "@/composables/chartPalette";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { MONTH_NAMES } from "@/constants";
import type { StackableDataset } from "@/types/ChartData";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { Modal } from "bootstrap";
import { computed, onMounted, ref, watch } from "vue";
import type { CompanyAllocation } from "@/admin/adminAllocationTypes";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

interface TopCompanies {
  ppa: CompanyAllocation[];
  forward: CompanyAllocation[];
  selfManaged: CompanyAllocation[];
  spotMarketPrice: CompanyAllocation[];
  spotMinPrice: CompanyAllocation[];
}

interface AllocationPlanResult {
  perMonth: Array<{
    monthIndex: number;
    year: number;
    production: number;
    ppa: number;
    forwardCommitted: number;
    forwardToBeSold: number;
    selfManaged: number;
    spotMarketPrice: number;
    spotMinPrice: number;
  }>;
  totals: {
    produced: number;
    ppa: number;
    forwardCommitted: number;
    forwardToBeSold: number;
    selfManaged: number;
    spotMarketPrice: number;
    spotMinPrice: number;
  };
  topCompanies?: TopCompanies;
}

interface Filters {
  country_codes: string[];
  technology_prefixes: string[];
  company_ids: number[];
  portfolio_types?: string[];
}

const props = defineProps<{
  filters: Filters;
}>();

const { globalErrorHandler } = useGlobalHandlers();

const isLoading = ref(false);
const allocationData = ref<AllocationPlanResult | null>(null);

const modalId = "allocationTopCompaniesModal";
const currentAllocationType = ref("");
const currentModalCompanies = ref<CompanyAllocation[]>([]);

const topCompanies = computed<TopCompanies>(() => {
  return (
    allocationData.value?.topCompanies || {
      ppa: [],
      forward: [],
      selfManaged: [],
      spotMarketPrice: [],
      spotMinPrice: [],
    }
  );
});

const allocationTotals = computed(() => {
  if (!allocationData.value) {
    return {
      ppa: 0,
      forward: 0,
      selfManaged: 0,
      spotMarketPrice: 0,
      spotMinPrice: 0,
      total: 0,
      ppaPercentage: 0,
      forwardPercentage: 0,
      selfManagedPercentage: 0,
      spotMarketPricePercentage: 0,
      spotMinPricePercentage: 0,
    };
  }

  const totals = allocationData.value.totals;
  const percentBase = totals.produced || 1;
  const displayTotal = totals.produced || 0;
  const forward =
    (totals.forwardCommitted || 0) + (totals.forwardToBeSold || 0);

  return {
    ppa: totals.ppa || 0,
    forward,
    selfManaged: totals.selfManaged || 0,
    spotMarketPrice: totals.spotMarketPrice || 0,
    spotMinPrice: totals.spotMinPrice || 0,
    total: displayTotal,
    ppaPercentage: ((totals.ppa || 0) / percentBase) * 100,
    forwardPercentage: (forward / percentBase) * 100,
    selfManagedPercentage: ((totals.selfManaged || 0) / percentBase) * 100,
    spotMarketPricePercentage:
      ((totals.spotMarketPrice || 0) / percentBase) * 100,
    spotMinPricePercentage: ((totals.spotMinPrice || 0) / percentBase) * 100,
  };
});

const chartData = computed(() => {
  if (!allocationData.value || !allocationData.value.perMonth) {
    return { labels: [], datasets: [] };
  }

  const monthNames = MONTH_NAMES;

  // Filter to show only next 12 months
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const next12Months = allocationData.value.perMonth.filter((month) => {
    const monthsSinceStart =
      (month.year - currentYear) * 12 + (month.monthIndex - currentMonth);
    return monthsSinceStart >= 0 && monthsSinceStart < 12;
  });

  const labels = next12Months.map((month) => {
    const monthIndex = month.monthIndex - 1;
    return month.year !== currentYear
      ? `${monthNames[monthIndex]} ${month.year}`
      : monthNames[monthIndex];
  });

  const datasets: StackableDataset[] = [
    {
      label: "PPA",
      data: next12Months.map((m) => m.ppa || 0),
      backgroundColor: PALETTE.semanticOverrides.portfolioType["PPA"],
      borderColor: PALETTE.semanticOverrides.portfolioType["PPA"],
      borderWidth: 0,
    },
    {
      label: "Forward",
      data: next12Months.map(
        (m) => (m.forwardCommitted || 0) + (m.forwardToBeSold || 0)
      ),
      backgroundColor: PALETTE.semanticOverrides.portfolioType["Forward"],
      borderColor: PALETTE.semanticOverrides.portfolioType["Forward"],
      borderWidth: 0,
    },
    {
      label: "Self-Managed",
      data: next12Months.map((m) => m.selfManaged || 0),
      backgroundColor: PALETTE.semanticOverrides.portfolioType["Self-managed"],
      borderColor: PALETTE.semanticOverrides.portfolioType["Self-managed"],
      borderWidth: 0,
    },
    {
      label: "Spot Market Price",
      data: next12Months.map((m) => m.spotMarketPrice || 0),
      backgroundColor: PALETTE.semanticOverrides.portfolioType["Spot Sales"],
      borderColor: PALETTE.semanticOverrides.portfolioType["Spot Sales"],
      borderWidth: 0,
    },
    {
      label: "Spot Min Price",
      data: next12Months.map((m) => m.spotMinPrice || 0),
      backgroundColor: PALETTE.categorical[3],
      borderColor: PALETTE.categorical[3],
      borderWidth: 0,
    },
  ];

  return { labels, datasets };
});

const fetchAllocationData = async () => {
  try {
    isLoading.value = true;

    const params = new URLSearchParams();

    props.filters.country_codes.forEach((code) =>
      params.append("country_codes", code)
    );
    props.filters.company_ids.forEach((id) =>
      params.append("company_ids", id.toString())
    );

    // Include portfolio_types filter if present
    if (props.filters.portfolio_types) {
      props.filters.portfolio_types.forEach((type: string) =>
        params.append("portfolio_types", type)
      );
    }

    // Include technology_prefixes filter if present
    if (props.filters.technology_prefixes) {
      props.filters.technology_prefixes.forEach((tech: string) =>
        params.append("technology_prefixes", tech)
      );
    }

    const response = await goSolidApi.get(
      `/api/admin/allocation/aggregated-simulation?${params.toString()}`
    );

    allocationData.value = response.data;
  } catch (error) {
    globalErrorHandler(error);
    allocationData.value = null;
  } finally {
    isLoading.value = false;
  }
};

const formatNumber = (value: number): string => {
  return value.toFixed(2);
};

const formatPercentage = (value: number): string => {
  const formatted = Number.isFinite(value)
    ? value.toFixed(2).padStart(5, "0")
    : "00.00";
  return `${formatted}%`;
};

const openModal = (type: string, companies: CompanyAllocation[]) => {
  currentAllocationType.value = type;
  currentModalCompanies.value = companies;

  const modalElement = document.getElementById(modalId);
  if (modalElement) {
    const modal = new Modal(modalElement);
    modal.show();
  }
};

// Watch for filter changes
watch(
  () => [
    props.filters.country_codes,
    props.filters.company_ids,
    props.filters.portfolio_types,
    props.filters.technology_prefixes,
  ],
  () => {
    fetchAllocationData();
  },
  { deep: true }
);

// Initial fetch when component mounts
onMounted(() => {
  fetchAllocationData();
});
</script>

<style scoped lang="scss">
.admin-allocation-chart {
  min-height: 400px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.chart-container {
  position: relative;
  background: white;
  padding: 1.5rem;
  border-radius: 1rem;
  border: 1px solid var(--phoenix-gray-200);
}

.chart-title {
  text-align: center;
  margin-bottom: 1.5rem;
}

.chart-title h4 {
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.allocation-legend {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--phoenix-gray-200);
}

.legend-item {
  text-align: center;
  padding: 0.75rem;
  background: var(--phoenix-gray-50);
  border-radius: 0.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.legend-item-total {
  background: linear-gradient(
    135deg,
    var(--phoenix-gray-100) 0%,
    var(--phoenix-gray-50) 100%
  );
  border: 2px solid var(--phoenix-gray-300);
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  display: inline-block;
  margin-right: 0.5rem;
}

.legend-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--phoenix-gray-700);
}

.legend-value {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--phoenix-gray-900);
  margin-top: 0.25rem;
}

.legend-percentage {
  font-size: 0.875rem;
  color: var(--phoenix-gray-600);
  margin-top: 0.125rem;
}
</style>
